import React, { SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { Autocomplete, Stack, TextField } from '@mui/material';
import { fieldToTextField, TextFieldProps } from 'formik-mui';
import { useMediaDropdown } from './hook';
import { MediaInfoDto } from '@sr/dto';
import { mergeAutocompleteInputSx } from 'shared/ui/autocomplete-input-sx';
import { getMediaLabel } from './lib';

interface Props {
  option: MediaInfoDto;
}


export const MediaOption = ({ option, ...props }: Props) => {
  const getLocationText = () => {
    if (option.country && !option.region && !option.city) {
      return `Страна: ${option.country?.name || option.country}`;
    }
    if (option.region && !option.city) {
      return `Регион: ${option.region?.name || option.region}`;
    }
    if (option.city) {
      return `Город: ${option.city?.name || option.city}`;
    }
    return 'Местоположение не указано';
  };
  return (
    <Stack {...props} direction="column" sx={{ alignItems: 'flex-start' }}>
      <span style={{ textAlign: 'left', width: '100%' }}>
        {getMediaLabel(option)}
      </span>
      <span style={{ textAlign: 'left', width: '100%', color: 'grey', fontSize: '0.875rem' }}>
        {getLocationText()} / Тип СМИ: {option.mediaType.name}
      </span>
    </Stack>
  );
};

export const MediaPickerField = (props: TextFieldProps) => {
  const {
    form: { setFieldValue },
    field: { name, value },
  } = props;

  const [open, setOpen] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<MediaInfoDto | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [inputValue, setInputValue] = useState('');
  const { list, loading: fetching } = useMediaDropdown(searchValue);

  const onChange = useCallback(
    (_event: SyntheticEvent<Element, Event>, newValue: MediaInfoDto | null) => {
      setSelectedMedia(newValue);
      setInputValue(newValue ? getMediaLabel(newValue) : '');
      setSearchValue('');
      setFieldValue(name, newValue?.id ?? null);
    },
    [setFieldValue, name, setSelectedMedia],
  );

  useEffect(() => {
    if (!value) {
      setSelectedMedia(null);
      setInputValue('');
      return;
    }

    const matchingMedia = list.find((item) => item.id === value);
    if (!matchingMedia) return;

    setSelectedMedia(matchingMedia);
    setInputValue(getMediaLabel(matchingMedia));
  }, [list, value]);


  const loading = open && fetching;

  return (
    <Autocomplete
      fullWidth
      value={selectedMedia}
      inputValue={inputValue}
      autoComplete={false}
      options={list}
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      onInputChange={(_event, newInputValue, reason) => {
        setInputValue(newInputValue);

        if (reason === 'input') {
          setSearchValue(newInputValue);
          return;
        }

        if (reason === 'clear') {
          setSearchValue('');
        }
      }}
      onChange={onChange}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={getMediaLabel}
      loading={loading}
      filterOptions={(options) => options}
      loadingText={'Загрузка...'}
      noOptionsText={'Ничего не найдено'}
      renderOption={(props, option) => <MediaOption {...props} option={option} />}
      renderInput={(params) => {
        const { sx, ...restFieldProps } = fieldToTextField(props);

        return (
          <TextField
            {...restFieldProps}
            name={name}
            {...params}
            label={props.label}
            size="small"
            placeholder="Начните набирать..."
            sx={mergeAutocompleteInputSx(sx)}
          />
        );
      }}
    />
  );
};
